import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { OrganizacionProvider } from './context/OrganizacionContext';
import ThemeProvider from './components/ThemeProvider';
import OTPLogin from './components/OTPLogin';
import LoginOTP from './pages/LoginOTP';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import DashboardLider from './pages/DashboardLider';
import EvaluacionForm from './pages/EvaluacionForm';
import LoginDashboard from './pages/LoginDashboard';
import Dashboard from './pages/DashboardWithLayout';
import LoginUnificado from './pages/LoginUnificado';
import EvaluacionEmpleadoForm from './components/EvaluacionEmpleadoForm';
import EvaluacionEmpleadoDinamica from './pages/EvaluacionEmpleadoDinamica';
import EvaluacionLiderDinamica from './pages/EvaluacionLiderDinamica';
import EmpleadoDashboard from './pages/EmpleadoDashboard';
import RoleSelection from './pages/RoleSelection';
import DashboardUnificado from './pages/DashboardUnificado';
import PersonalManagement from './components/admin/PersonalManagement';
import PreguntasManagement from './components/admin/PreguntasManagement';
import ReportesManagement from './components/admin/ReportesManagement';
import ConfiguracionManagement from './components/admin/ConfiguracionManagement';
import ComentariosEvaluacion from './components/admin/ComentariosEvaluacion';
import ConfiguracionOrganizacion from './components/ConfiguracionOrganizacion';
import TestEvaluacion360 from './pages/Evaluacion360Page';
import CargaMasivaPage from './pages/CargaMasivaPage';
import CargaMasivaPreguntas from './components/CargaMasivaPreguntas';

// Componente para proteger rutas de líder
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return isAuthenticated() ? children : <Navigate to="/" />;
};

// Componente que maneja el login OTP
const OTPLoginWrapper = () => {
  const { loginLider } = useAuth();
  const navigate = useNavigate();

  const handleOTPLoginSuccess = (userInfo) => {
    // Verificar si el usuario es un líder
    if (userInfo.tipo === "lider" && userInfo.lider_info) {
      // Es un líder, usar la información de líder
      const liderData = {
        id: userInfo.lider_info.id,
        nombre_completo: userInfo.lider_info.nombre_completo,
        documento_identidad: userInfo.lider_info.documento_identidad,
        email: userInfo.lider_info.email
      };
      
      // Usar los empleados a cargo de la respuesta
      const empleados = userInfo.empleados_a_cargo || [];
      
      loginLider(liderData, empleados);
      navigate('/dashboard-lider');
    } else {
      // Es solo un empleado, crear estructura básica de líder
      const liderData = {
        nombre_completo: userInfo.nombre,
        documento_identidad: userInfo.cedula,
        email: userInfo.email
      };
      
      // Sin empleados a cargo
      const empleados = [];
      
      loginLider(liderData, empleados);
      navigate('/dashboard-lider');
    }
  };

  return <OTPLogin onLoginSuccess={handleOTPLoginSuccess} />;
};

function App() {
  return (
    <AuthProvider>
      <OrganizacionProvider>
        <ThemeProvider>
          <Router>
            <div className="App">
              <Routes>
            {/* Ruta principal - Login unificado */}
            <Route path="/" element={<LoginUnificado />} />
            
            {/* Login OTP (acceso seguro con email) */}
            <Route path="/login-otp" element={<LoginOTP />} />
            
            {/* Rutas protegidas para líderes */}
            <Route 
              path="/dashboard-lider" 
              element={
                <ProtectedRoute>
                  <DashboardLider />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/evaluacion/:empleadoId" 
              element={
                <ProtectedRoute>
                  <EvaluacionForm />
                </ProtectedRoute>
              } 
            />
            
            {/* Nuevas rutas de evaluación */}
            <Route path="/login-unificado" element={<LoginUnificado />} />
            <Route path="/dashboard-unificado" element={<DashboardUnificado />} />
            <Route path="/role-selection" element={<RoleSelection />} />
            <Route path="/empleado/dashboard" element={<EmpleadoDashboard />} />
            <Route path="/evaluacion-form" element={<EvaluacionEmpleadoForm />} />
            <Route path="/evaluacion-empleado-dinamica" element={<EvaluacionEmpleadoDinamica />} />
            <Route path="/evaluacion-lider-dinamica" element={<EvaluacionLiderDinamica />} />
            <Route path="/test-evaluacion360" element={<TestEvaluacion360 />} />
            
            {/* Rutas del dashboard administrativo */}
            <Route path="/login-dashboard" element={<LoginDashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            
            {/* Rutas de administración */}
            <Route 
              path="/admin/personal" 
              element={
                <AdminProtectedRoute>
                  <PersonalManagement />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/admin/preguntas" 
              element={
                <AdminProtectedRoute>
                  <PreguntasManagement />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/admin/reportes" 
              element={
                <AdminProtectedRoute>
                  <ReportesManagement />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/admin/configuracion" 
              element={
                <AdminProtectedRoute>
                  <ConfiguracionManagement />
                </AdminProtectedRoute>
              } 
            />
            <Route 
              path="/admin/comentarios" 
              element={
                <AdminProtectedRoute>
                  <ComentariosEvaluacion />
                </AdminProtectedRoute>
              } 
            />
            
            {/* Ruta de carga masiva */}
            <Route 
              path="/carga-masiva" 
              element={
                <AdminProtectedRoute>
                  <CargaMasivaPage />
                </AdminProtectedRoute>
              } 
            />
            
            {/* Ruta de carga masiva de preguntas */}
            <Route 
              path="/carga-masiva-preguntas" 
              element={
                <AdminProtectedRoute>
                  <CargaMasivaPreguntas />
                </AdminProtectedRoute>
              } 
            />
            
            {/* Ruta de configuración de organización */}
            <Route 
              path="/configuracion" 
              element={
                <AdminProtectedRoute>
                  <ConfiguracionOrganizacion />
                </AdminProtectedRoute>
              } 
            />
            
            {/* Ruta catch-all */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          
          {/* Contenedor de notificaciones */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </Router>
        </ThemeProvider>
      </OrganizacionProvider>
    </AuthProvider>
  );
}

export default App;
